
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { toast } from 'sonner';

interface PageAccessStatus {
  [key: string]: boolean;
}

interface PageAccessRecord {
  page_name: string;
  is_locked: boolean;
  locked_by: string | null;
  locked_at: string | null;
}

export const usePageAccess = () => {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [pageAccess, setPageAccess] = useState<PageAccessStatus>({});
  const [loading, setLoading] = useState(true);

  const fetchPageAccess = async () => {
    try {
      console.log('🔍 Fetching page access data...');
      setLoading(true);
      const { data, error } = await supabase
        .from('page_access')
        .select('page_name, is_locked, locked_by, locked_at');

      if (error) {
        console.error('❌ Error fetching page access:', error);
        toast.error('Failed to load page access settings');
        return;
      }

      console.log('📊 Raw page access data:', data);

      const accessMap = data.reduce((acc: PageAccessStatus, item: PageAccessRecord) => {
        acc[item.page_name] = item.is_locked;
        return acc;
      }, {});

      console.log('🗂️ Processed page access map:', accessMap);
      setPageAccess(accessMap);
    } catch (error) {
      console.error('💥 Error in fetchPageAccess:', error);
      toast.error('Failed to load page access settings');
    } finally {
      setLoading(false);
    }
  };

  const updatePageAccess = async (pageName: string, isLocked: boolean) => {
    if (!isAdmin) {
      toast.error('Only admins can update page access');
      return false;
    }

    console.log(`🔄 Updating page access: ${pageName} -> ${isLocked ? 'LOCKED' : 'UNLOCKED'}`);

    try {
      const { error } = await supabase.rpc('update_page_access', {
        page_name_input: pageName,
        is_locked_input: isLocked
      });

      if (error) {
        console.error('❌ Error updating page access:', error);
        toast.error(`Failed to ${isLocked ? 'lock' : 'unlock'} ${pageName}`);
        return false;
      }

      // Update local state immediately for better UX
      setPageAccess(prev => {
        const updated = {
          ...prev,
          [pageName]: isLocked
        };
        console.log('✅ Local state updated:', updated);
        return updated;
      });

      toast.success(`${pageName} ${isLocked ? 'locked' : 'unlocked'} successfully`);
      return true;
    } catch (error) {
      console.error('💥 Error in updatePageAccess:', error);
      toast.error('Failed to update page access');
      return false;
    }
  };

  const lockAllPages = async () => {
    if (!isAdmin) {
      toast.error('Only admins can lock pages');
      return false;
    }

    console.log('🔒 Locking all pages...');

    try {
      const { error } = await supabase.rpc('lock_all_pages');

      if (error) {
        console.error('❌ Error locking all pages:', error);
        toast.error('Failed to lock all pages');
        return false;
      }

      // Update local state - set all pages to locked
      const updatedAccess = Object.keys(pageAccess).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as PageAccessStatus);

      console.log('🔒 All pages locked - local state:', updatedAccess);
      setPageAccess(updatedAccess);
      toast.success('All pages locked successfully');
      return true;
    } catch (error) {
      console.error('💥 Error in lockAllPages:', error);
      toast.error('Failed to lock all pages');
      return false;
    }
  };

  const isPageLocked = (pageName: string): boolean => {
    // Admins always have access
    if (isAdmin) return false;
    
    const locked = pageAccess[pageName] || false;
    console.log(`🔍 Checking access for ${pageName}: ${locked ? 'LOCKED' : 'UNLOCKED'} (admin: ${isAdmin})`);
    return locked;
  };

  useEffect(() => {
    fetchPageAccess();

    // Set up real-time subscription for page access changes
    console.log('🔄 Setting up real-time subscription for page access changes...');
    const channel = supabase
      .channel('page-access-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'page_access'
        },
        (payload) => {
          console.log('🔔 Real-time page access change:', payload);
          fetchPageAccess();
        }
      )
      .subscribe();

    return () => {
      console.log('🧹 Cleaning up page access subscription');
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  return {
    pageAccess,
    loading,
    updatePageAccess,
    lockAllPages,
    isPageLocked,
    refetch: fetchPageAccess
  };
};
